import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from '../../components/ui/use-toast';
import organizationsClient from '../organizations-client';
import {
    organizationAnalyticsSchema,
    organizationSchema,
    type Organization,
    type OrganizationCreateInput,
    type OrganizationUpdateInput,
} from '../schemas/organization.schema';

const ORGANIZATIONS_QUERY_KEY = 'organizations';

export function useOrganizations() {
    return useQuery({
        queryKey: [ORGANIZATIONS_QUERY_KEY],
        queryFn: async () => {
            const response = await organizationsClient.get<Organization[]>('/organizations');
            return response.data.map((org) => organizationSchema.parse(org));
        },
    });
}

export function useOrganization(organizationId: string) {
    return useQuery({
        queryKey: [ORGANIZATIONS_QUERY_KEY, organizationId],
        queryFn: async () => {
            const response = await organizationsClient.get<Organization>(`/organizations/${organizationId}`);
            return organizationSchema.parse(response.data);
        },
        enabled: !!organizationId,
    });
}

export function useOrganizationAnalytics(organizationId: string) {
    return useQuery({
        queryKey: [ORGANIZATIONS_QUERY_KEY, organizationId, 'analytics'],
        queryFn: async () => {
            const response = await organizationsClient.get(`/organizations/${organizationId}/analytics`);
            return organizationAnalyticsSchema.parse(response.data);
        },
        enabled: !!organizationId,
    });
}

export function useCreateOrganization() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (input: OrganizationCreateInput) => {
            const response = await organizationsClient.post<Organization>('/organizations', {
                ...input,
                accounts: [],
                admins: [],
            });
            return organizationSchema.parse(response.data);
        },
        onSuccess: (data) => {
            void queryClient.invalidateQueries({ queryKey: [ORGANIZATIONS_QUERY_KEY] });
            toast({
                title: 'Success',
                description: `Organization "${data.name}" created successfully`,
            });
        },
        onError: (error: Error) => {
            toast({
                variant: 'destructive',
                title: 'Error',
                description: error.message || 'Failed to create organization',
            });
        },
    });
}

export function useUpdateOrganization() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ organizationId, input }: { organizationId: string; input: OrganizationUpdateInput }) => {
            const response = await organizationsClient.patch<Organization>(`/organizations/${organizationId}`, input);
            return organizationSchema.parse(response.data);
        },
        onSuccess: (data) => {
            void queryClient.invalidateQueries({ queryKey: [ORGANIZATIONS_QUERY_KEY] });
            toast({
                title: 'Success',
                description: `Organization "${data.name}" updated successfully`,
            });
        },
        onError: (error: Error) => {
            toast({
                variant: 'destructive',
                title: 'Error',
                description: error.message || 'Failed to update organization',
            });
        },
    });
}

export function useDeleteOrganization() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (organizationId: string) => {
            await organizationsClient.delete(`/organizations/${organizationId}`);
        },
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [ORGANIZATIONS_QUERY_KEY] });
            toast({
                title: 'Success',
                description: 'Organization deleted successfully',
            });
        },
        onError: (error: Error) => {
            toast({
                variant: 'destructive',
                title: 'Error',
                description: error.message || 'Failed to delete organization',
            });
        },
    });
}
